import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'article-1': 'https://readdy.ai/api/search-image?query=Chinese%20website%20registration%20page%20on%20laptop%20screen%20with%20clean%20modern%20design%2C%20warm%20lighting%2C%20minimal%20interface%2C%20professional%20atmosphere&width=800&height=400&seq=article-1&orientation=landscape',
  'article-2': 'https://readdy.ai/api/search-image?query=Password%20reset%20security%20concept%20on%20a%20modern%20website%20interface%2C%20warm%20neutral%20tones%2C%20clean%20UI%20design%2C%20professional%20tech%20atmosphere&width=800&height=400&seq=article-2&orientation=landscape',
  'article-3': 'https://readdy.ai/api/search-image?query=Cybersecurity%20concept%20with%20shield%20icon%20on%20digital%20screen%2C%20warm%20professional%20atmosphere%2C%20clean%20modern%20design&width=800&height=400&seq=article-3&orientation=landscape',
  'article-4': 'https://readdy.ai/api/search-image?query=Digital%20platform%20features%20overview%20on%20a%20clean%20modern%20interface%2C%20warm%20professional%20design%2C%20multiple%20feature%20icons&width=800&height=400&seq=article-4&orientation=landscape',
  'article-5': 'https://readdy.ai/api/search-image?query=Search%20engine%20optimization%20concept%20on%20a%20digital%20screen%2C%20warm%20lighting%2C%20clean%20modern%20interface%2C%20professional%20design&width=800&height=400&seq=article-5&orientation=landscape',
  'article-6': 'https://readdy.ai/api/search-image?query=Official%20announcement%20on%20a%20website%20with%20megaphone%20icon%2C%20warm%20professional%20atmosphere%2C%20clean%20modern%20design&width=800&height=400&seq=article-6&orientation=landscape',
  'article-7': 'https://readdy.ai/api/search-image?query=Privacy%20policy%20and%20data%20protection%20concept%20on%20a%20digital%20screen%2C%20warm%20professional%20design%2C%20lock%20icon&width=800&height=400&seq=article-7&orientation=landscape',
  'article-8': 'https://readdy.ai/api/search-image?query=Terms%20of%20service%20document%20on%20a%20desk%20with%20warm%20lighting%2C%20professional%20legal%20atmosphere%2C%20clean%20modern%20design&width=800&height=400&seq=article-8&orientation=landscape',
  'article-9': 'https://readdy.ai/api/search-image?query=Troubleshooting%20guide%20concept%20on%20a%20laptop%20screen%2C%20warm%20professional%20atmosphere%2C%20question%20mark%20icon&width=800&height=400&seq=article-9&orientation=landscape',
  'article-10': 'https://readdy.ai/api/search-image?query=Phishing%20website%20warning%20concept%20with%20danger%20icon%20on%20digital%20screen%2C%20warm%20professional%20atmosphere&width=800&height=400&seq=article-10&orientation=landscape',
  'article-11': 'https://readdy.ai/api/search-image?query=Personalization%20settings%20interface%20on%20a%20modern%20website%2C%20warm%20professional%20design%2C%20clean%20and%20minimal&width=800&height=400&seq=article-11&orientation=landscape',
  'article-12': 'https://readdy.ai/api/search-image?query=Email%20verification%20concept%20on%20a%20laptop%2C%20warm%20professional%20atmosphere%2C%20envelope%20icon%2C%20modern%20design&width=800&height=400&seq=article-12&orientation=landscape',
};

export default function CategoryPage() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const currentCategory = categories.find((c) => c.id === categoryId);
  const categoryArticles = useMemo(() => {
    if (!categoryId) return [];
    return articles.filter((a) => a.category === categoryId).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [categoryId]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <h2 className="mt-4 text-xl font-semibold text-foreground-950">分类未找到</h2>
            <p className="mt-2 text-sm text-foreground-500">请检查分类地址是否正确</p>
            <Link to="/" className="mt-6 inline-block px-6 py-2 bg-primary-500 text-background-50 rounded-md text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const titleStr = `${currentCategory.name} - 91n.com 使用指南与教程`;
  const descStr = `${currentCategory.description}。浏览 91n.com ${currentCategory.name}相关文章，获取最新教程和官方解答。`;

  return (
    <>
      <ScrollToTop />
      <SEO
        title={titleStr}
        description={descStr}
        keywords={`91n.com,91n.com${currentCategory.name},${currentCategory.name}指南,91n.com教程`}
        canonicalUrl={`/category/${categoryId}`}
      />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebPage" data={{ name: titleStr, description: descStr }} />
      <SchemaOrg type="BreadcrumbList" />

      <Navbar />

      <main className="pt-16">
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb />
            <div className="flex items-center gap-4 mt-2">
              <div className={`w-14 h-14 flex items-center justify-center rounded-full ${
                currentCategory.color === 'primary' ? 'bg-primary-50 text-primary-500' :
                currentCategory.color === 'accent' ? 'bg-accent-50 text-accent-500' :
                'bg-secondary-50 text-secondary-500'
              }`}>
                <i className={`${currentCategory.icon} text-2xl`}></i>
              </div>
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  {currentCategory.name}
                </h2>
                <p className="mt-1 text-sm text-foreground-500">{currentCategory.description}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    cat.id === categoryId
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200 transition-all group cursor-pointer"
                  >
                    <div className="h-48 overflow-hidden">
                      <img
                        src={articleImages[article.id]}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-3 text-xs text-foreground-400 mb-2">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {article.readTime}
                        </span>
                        <span>{article.author}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">{article.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500 text-sm">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}